'use client';

import { Dialog, Transition } from '@headlessui/react';
import React, { Suspense, useRef } from 'react';
import { AddressFormValues } from '~/components/Address/AddressForm';
import AddressModal from '~/components/Address/AddressModal';
import MakeOfferModal from '~/components/ItemDetails/MakeOfferModal';
import dynamicModal from '~/components/Modal/dynamicModal';
import BaseNewModal from '~/components/NewModal/BaseNewModal';
import { useModals } from '~/contexts/ModalsContext';
import { NewModalContext } from '~/contexts/NewModalContext';
import { AnalyticsSource } from '~/services/analytics/AnalyticsInterfaces';
import { ListFlowImage } from '~/store/listFlow/slices/photos';
import { CommercePaymentMethod } from '~/typings/services/commerce/payments';
import { CommerceTransferMethod } from '~/typings/services/commerce/sellerAccount';
import { IAMLoginMeta, SocialData } from '~/typings/services/iam/auth';
import {
  RailsCategory,
  RailsItem,
  RailsThumbnailItem,
} from '~/typings/services/rails/item';
import { RailsSavedSearch } from '~/typings/services/rails/savedSearch';
import { RailsAddress } from '~/typings/services/rails/session';

export type ModalProps<T extends keyof Modals> = Modals[T] & {
  onRequestClose: () => void;
};

export interface Modals {
  cashoutInitiated: {
    email?: string;
    cashoutAmount?: number;
    method?: CommerceTransferMethod;
    type: 'PAYONEER' | 'GIFT_CARD' | 'VENMO' | 'DONATE';
    teamName?: string;
  };
  address: {
    editAddress?: RailsAddress;
    defaultFlags?: Partial<AddressFormValues['flags']>;
    neverShowCheckboxes?: boolean;
    onSuccessfulSubmit?: (address: RailsAddress) => void;
  };
  bankInfoHelp: {
    country: string;
  };
  categorySelector: {
    categories?: RailsCategory[];
    onChange: (category: RailsCategory) => void;
    selectedCategoryId?: number | string;
  };
  makeOffer: {
    action:
      | 'makeOffer'
      | 'buyNow'
      | 'addToCart'
      | 'sellerOffer'
      | 'counterOffer';
    item: RailsItem;
    onSuccessfullyMadeOffer?: () => void;
    swapId?: number;
    selectedVariationId?: string | number;
    betaCheckout?: boolean;
  };
  itemPublished: {
    item: RailsItem;
  };
  removeReport: {
    item?: RailsItem | RailsThumbnailItem;
    onSuccessfulSubmit?: () => void;
    type: 'reportItem' | 'removeItem';
  };
  priceDrop: {
    item: RailsItem | RailsThumbnailItem;
    overrideSubmitFunction?: (price: number) => void;
    onSuccessfulSubmit?: () => void;
  };
  priceDropBulk: {
    onDropPrice: (percentage: number) => void;
    onSuccessfulSubmit?: () => void;
  };
  addedToCart: {
    item: RailsItem;
    selectedVariationId?: string;
  };
  loginRegister: {
    defaultView?:
      | 'login'
      | 'register'
      | 'registerForm'
      | 'resetPassword'
      | 'challenge'
      | 'socialExistingEmail'
      | 'socialNewUser';
    contextMessage?: string;
    initialChallengeData?: {
      token: string;
      meta: IAMLoginMeta;
    } | null;
    initialSocialData?: SocialData | null;
    onSuccessfulLogin?: () => void;
    skipOnboarding?: boolean;
    giveawayCopyTest?: boolean;
    src?: AnalyticsSource;
    eventProperties?: Record<any, any>;
  };
  savedToFavoritesGuidance: {
    itemId: number;
  };
  paymentMethod: {
    editingPaymentMethod?: CommercePaymentMethod;
    onClose?: () => void;
    onSuccessfulSubmit?: () => void;
  };
  vacationMode: {
    onSuccessfulSubmit?: () => void;
  };
  editSavedSearch: {
    onSuccessfulDelete?: () => void;
    onSuccessfulSave?: () => void;
    recentlyCreated?: boolean;
    savedSearch: RailsSavedSearch;
  };
  bumpsGuidance: {
    itemId?: number;
    onSuccessfulBump?: () => void;
  };
  bumpsPurchase: {
    onSuccessfulPurchase?: () => void;
  };
  proBumpsGuidance: {
    itemId?: number;
    onSuccessfulBump?: () => void;
  };
  saleGuidance: {};
  publishAll: {};
  addAllItemsToSale: {};
  avatarUpload: {
    initialAvatarUrl?: string | null;
  };
  redeemGiftCard: {};
  barstoolEventEmailCapture: {
    avatarUrl?: string | null;
    username: string;
  };
  selfUnrestrict: {};
  gearExpertsPopup: {};
  howToBundle: {
    itemId: number;
  };
  prizeoutDateOfBirthConfirmationModal: {
    onSuccessfulSubmit: (birthYear: number) => void;
  };
  sendMessageVerifyPhone: {
    // If the user is already verified when opening the modal, immediately fire onVerificationSuccess
    allowAutoSuccess?: boolean;
    onVerificationSuccess?: () => void;
  };
  photoEditModal?: {
    onSave?: () => void;
    index: number;
    photo: ListFlowImage;
  };
  variationsModal?: {
    editingIndex?: number;
    onSuccess?: () => void;
  };
  listFlowEarningsModal?: {};
}

function getModal<T extends ModalType>(type: T): any | never {
  switch (type) {
    case 'cashoutInitiated':
      return dynamicModal(
        () =>
          import('~/components/SellerDashboard/Cashouts/CashoutInitiatedModal'),
      );

    case 'address':
      return AddressModal;

    case 'bankInfoHelp':
      return dynamicModal(
        () => import('~/components/SellerDashboard/BankInfoHelpModal'),
      );

    case 'categorySelector':
      return dynamicModal(
        () => import('~/components/Form/CategorySelectorModal'),
      );

    case 'makeOffer':
      return dynamicModal(
        () => import('~/components/ItemDetails/MakeOfferModal'),
      );
      return MakeOfferModal;

    case 'itemPublished':
      return dynamicModal(
        () => import('~/components/ItemDetails/ItemPublishedModal'),
      );

    case 'removeReport':
      return dynamicModal(
        () => import('~/components/ItemDetails/RemoveReportModal'),
      );

    case 'priceDrop':
      return dynamicModal(
        () => import('~/components/ItemDetails/PriceDropModal'),
      );

    case 'priceDropBulk':
      return dynamicModal(
        () => import('~/components/NewLockers/BulkActions/BulkPriceDropModal'),
      );

    case 'addedToCart':
      return dynamicModal(
        () => import('~/components/ItemDetails/AddedToCartModal'),
      );

    case 'loginRegister':
      return dynamicModal(
        () => import('~/components/LoginRegister/LoginRegisterModal'),
      );

    case 'savedToFavoritesGuidance':
      return dynamicModal(
        () => import('~/components/ItemDetails/SavedToFavoritesGuidanceModal'),
      );

    case 'paymentMethod':
      return dynamicModal(
        () => import('~/components/Settings/PaymentMethodModal'),
      );

    case 'vacationMode':
      return dynamicModal(
        () => import('~/components/Settings/VacationModeModal'),
      );

    case 'editSavedSearch':
      return dynamicModal(
        () => import('~/components/SavedSearch/EditSavedSearchModal'),
      );

    case 'bumpsGuidance':
      return dynamicModal(
        () => import('~/components/Bumps/BumpsGuidanceModal'),
      );

    case 'bumpsPurchase':
      return dynamicModal(
        () => import('~/components/Bumps/BumpsPurchaseModal'),
      );

    case 'saleGuidance':
      return dynamicModal(() => import('~/components/Sales/SaleGuidanceModal'));

    case 'publishAll':
      return dynamicModal(
        () => import('~/components/NewLockers/PublishAllModal'),
      );

    case 'addAllItemsToSale':
      return dynamicModal(
        () => import('~/components/NewLockers/AddAllItemsToSaleModal'),
      );

    case 'avatarUpload':
      return dynamicModal(
        () => import('~/components/NewLockers/AvatarUpload/AvatarUploadModal'),
      );

    case 'redeemGiftCard':
      return dynamicModal(
        () => import('~/components/GiftCards/RedeemGiftCardModal'),
      );

    case 'barstoolEventEmailCapture':
      return dynamicModal(
        () => import('~/components/NewLockers/BarstoolEventEmailCaptureModal'),
      );

    case 'selfUnrestrict':
      return dynamicModal(
        () => import('~/components/Chrome/Banners/SelfUnrestrictModal'),
      );

    case 'gearExpertsPopup':
      return dynamicModal(
        () => import('~/components/GearExperts/GearExpertsPopup'),
      );

    case 'howToBundle':
      return dynamicModal(
        () => import('~/components/ItemDetails/HowToBundleModal'),
      );

    case 'prizeoutDateOfBirthConfirmationModal':
      return dynamicModal(
        () =>
          import(
            '~/components/SellerDashboard/Prizeout/PrizeoutDateOfBirthConfirmationModal'
          ),
      );

    case 'sendMessageVerifyPhone':
      return dynamicModal(
        () =>
          import(
            '~/components/AccountVerifications/SendMessageVerifyPhoneModal'
          ),
      );

    case 'photoEditModal':
      return dynamicModal(
        () => import('~/components/ListFlow/PhotoSection/PhotoEditModal'),
      );

    case 'variationsModal':
      return dynamicModal(
        () =>
          import(
            '~/components/ListFlow/InfoSection/Variations/ListFlowVariationsOptionsModal'
          ),
      );

    case 'listFlowEarningsModal':
      return dynamicModal(
        () =>
          import(
            '~/components/ListFlow/PriceSection/ListFlowCalculateEarningsModal'
          ),
      );

    case 'proBumpsGuidance':
      return dynamicModal(
        () => import('~/components/SidelinePro/ProBumpsModal'),
      );

    default:
      alert(`Invalid modal type specified, "${type}"`);
      throw '';
  }
}

export type ModalType = keyof Modals;

const ModalsGroup = () => {
  const { closeModal, deleteModal, modals } = useModals();

  const modalComponentCache = useRef<Record<number, React.ReactElement>>({});
  const modalComponents: Record<number, React.ReactElement> = modals.reduce(
    (prev, cur) => {
      const onRequestClose = () => closeModal(cur.id);
      let component;
      if (typeof cur.component === 'function') {
        component = cur.component(onRequestClose);
      } else if (React.isValidElement(cur.component)) {
        component = cur.component;
      } else {
        component = React.createElement(getModal(cur.type), {
          ...cur.props,
          onRequestClose,
        });
      }

      if (!modalComponentCache.current[cur.id]) {
        modalComponentCache.current[cur.id] = component;
      }

      return {
        ...prev,
        [cur.id]: modalComponentCache.current[cur.id],
      };
    },
    {},
  );

  const renderedModals = modals.map(modal => {
    const onRequestClose = () => closeModal(modal.id);
    const component = modalComponents[modal.id];

    return (
      <Transition
        key={modal.id}
        as={React.Fragment}
        show={modal.open}
        appear
        afterLeave={() => deleteModal(modal.id)}
      >
        <BaseNewModal open={modal.open} onClose={onRequestClose}>
          <NewModalContext
            value={{ onClose: onRequestClose, open: modal.open }}
          >
            <Suspense>
              {React.cloneElement(component, {
                // @ts-expect-error
                ...component.props,
                ...modal.props,
              })}
            </Suspense>
          </NewModalContext>
        </BaseNewModal>
      </Transition>
    );
  });

  if (modals.length > 2) {
    return (
      <Dialog static open={modals.some(m => m.open)} onClose={() => {}}>
        {renderedModals}
      </Dialog>
    );
  }

  return (
    // Hack with headless UI to avoid scroll lock from opening/closing multiple modals at the same time
    <>{renderedModals}</>
  );
};

export default ModalsGroup;
